import React, { Component } from "react";
import { Map as ReactMap, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { tableFromIPC } from "apache-arrow";
import { loadGeo } from "./utils/mapPage/loadGeo";
import { fetchMetrics } from "./utils/mapPage/fetchMetrics";
import { restyleGeo } from "./utils/mapPage/restyleGeo";
import { buildLabelsAndColors, rowsFromTable, makeHeatScale, make2pointHeatScale } from "./utils/mapPage/helpers";
import { precomputeCentroids, buildCompareArrows, registerArrowImage } from "./buildCompareArrows";
import MapHoverPopup from "./MapHoverPopup";
import { getAggregatesForSidebar } from "./utils/mapPage/getAggregatesForSidebar";
import { fetchElectoratesBaseline } from "./utils/mapPage/fetchElectoratesBaseline";
import MapToolbar from "./MapToolbar";

const MAPBOX_TOKEN = "pk.eyJ1IjoibXppZW1pYW4iLCJhIjoiY2xhOXdpc2h5MDI1MzNxbG1wdzcxOWNlZSJ9.Km0c9-jUUlcBs6ByXYbYZA";
const MAP_STYLE = "mapbox://styles/mapbox/light-v11";
const NZ_BOUNDS = [[166.0, -47.5], [179.0, -33.0]];

class MapPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      geoData: null,
      styledGeo: null,
      metricRows: null,
      loading: false,
      styleLoaded: false,
      hoverInfo: null,
      selectedFeature: null,
      selectedBounds: null,
      selectedConstCode: null,
      selectedConstName: null,
      metricRowsElectorates: null,
      centroids: null,
      compareArrows: null,
    };
    this.mapRef = React.createRef();
    this._internals = { inflight: null, lastKey: null, lastAt: 0 };
    this._electorateBaseline = { inflight: null, lastKey: null, lastAt: 0 };
  }

  componentDidMount() {
    this.loadGeo(this.props.geoLevel || "electorate");
    this.fetchMetrics(this.props.geoLevel || "electorate");
    this.fetchElectoratesBaseline();
    window.addEventListener("resize", this.fitNZ);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.fitNZ);
  }

  fitNZ = () => {
    const map = this.mapRef.current?.getMap?.();
    if (map) map.fitBounds(NZ_BOUNDS, { padding: 24, duration: 600 });
  };

  componentDidUpdate(prevProps, prevState) {
    const levelChanged = prevProps.geoLevel !== this.props.geoLevel;
    const groupChanged = prevProps.selectedGroupId !== this.props.selectedGroupId;
    const filtersChanged = JSON.stringify(this.getFilters()) !== JSON.stringify(
      { gender: prevProps.genderFilter, age: prevProps.ageFilter,
        ethnicity: prevProps.ethnicityFilter, party: prevProps.partyFilter,
        education: prevProps.educationFilter }
    );
    const prefChanged = prevProps.preferenceFilter !== this.props.preferenceFilter;
    const waveChanged = prevProps.waveSelect !== this.props.waveSelect;
    const compareChanged = prevProps.compareWaves !== this.props.compareWaves;
    const targetVarChanged =
      prevProps.ActionTargetVariables?.TargetVariable1 !== this.props.ActionTargetVariables?.TargetVariable1;
    const targetSubVarChanged =
      prevProps.ActionTargetSubVariables?.TargetSubVariable1 !== this.props.ActionTargetSubVariables?.TargetSubVariable1;
    const concentrationChanged =
      prevProps.ActionTargetConcentration !== this.props.ActionTargetConcentration;
    const actionModeChanged = prevProps.ActionMode !== this.props.ActionMode;

    if (levelChanged) {
      this.loadGeo(this.props.geoLevel);
      this.fetchMetrics(this.props.geoLevel);
      return;
    }

    if (groupChanged || filtersChanged || targetVarChanged || actionModeChanged) {
      this.fetchMetrics(this.props.geoLevel || "electorate");
      this.fetchElectoratesBaseline();
    }

    const shouldRestyle =
      prevState.metricRows !== this.state.metricRows ||
      prevState.geoData !== this.state.geoData ||
      groupChanged || prefChanged || waveChanged ||
      targetVarChanged || targetSubVarChanged || concentrationChanged || actionModeChanged;

    if (shouldRestyle) this.restyle();

    if (prevState.geoData !== this.state.geoData && this.state.geoData?.features) {
      const centroids = precomputeCentroids(this.state.geoData.features);
      this.setState({ centroids });
    }

    if (compareChanged || waveChanged || prevState.centroids !== this.state.centroids) {
      this.buildArrows();
    }

    if (this.props.selectedAreaIdToZoom && this.props.selectedAreaIdToZoom !== prevProps.selectedAreaIdToZoom) {
      this.zoomToAreaId(this.props.selectedAreaIdToZoom);
    }

    const sidebarDataChanged =
      prevState.metricRows !== this.state.metricRows ||
      prevState.metricRowsElectorates !== this.state.metricRowsElectorates ||
      prevState.selectedFeature !== this.state.selectedFeature ||
      prevState.selectedConstCode !== this.state.selectedConstCode ||
      prevState.selectedConstName !== this.state.selectedConstName ||
      groupChanged ||
      waveChanged;

    if (sidebarDataChanged) {
      const data = this.computeSidebarAggregates();
      this.props.onFeatureSelect?.({
        feature: this.state.selectedFeature,
        data,
        selectedConstName: this.state.selectedConstName,
      });
    }
  }

  computeSidebarAggregates = () => {
    return getAggregatesForSidebar({
      metricRows: this.state.metricRows,
      metricRowsElectorates: this.state.metricRowsElectorates,
      selectedFeature: this.state.selectedFeature,
      selectedConstCode: this.state.selectedConstCode,
      group: this.getActiveGroup(),
      waveSelect: this.props.waveSelect,
    });
  };

  fetchElectoratesBaseline = () => {
    fetchElectoratesBaseline({
      getActiveGroup: this.getActiveGroup,
      getFilters: this.getFilters,
      getKeyWeightMap: this.getKeyWeightMap,
      setState: this.setState.bind(this),
      _tracking: this._electorateBaseline,
    });
  };

  loadGeo = (level) => {
    loadGeo({
      level,
      doFit: this.state.selectedConstCode == null,
      setState: this.setState.bind(this),
      mapRef: this.mapRef,
      handlePossibleAreasChange: this.props.handlePossibleAreasChange,
    });
  };

  getFilters = () => ({
    gender:    this.props.genderFilter    ?? null,
    age:       this.props.ageFilter       ?? null,
    ethnicity: this.props.ethnicityFilter ?? null,
    party:     this.props.partyFilter     ?? null,
    education: this.props.educationFilter ?? null,
  });

  getGroups = () => this.props.groupsById || {};

  getActiveGroup = () => {
    const groupsById = this.getGroups();
    if (this.props.ActionMode === "filter") {
      const t = this.props.ActionTargetVariables || {};
      const tv1 = groupsById?.[t.TargetVariable1] || null;
      return { TargetVariable1: tv1, schema: tv1?.schema || null };
    }
    const { selectedGroupId } = this.props;
    return groupsById[selectedGroupId] || null;
  };

  requestKey = (level) => {
    const group = this.getActiveGroup();
    const effectiveGroup = group?.TargetVariable1 || group;
    const filters = this.getFilters();
    const cc = (level === "SA3" || level === "SA2") ? (this.state.selectedConstCode ?? "") : "";
    return [level, effectiveGroup?.label || "", JSON.stringify(filters), cc].join("|");
  };

  getKeyWeightMap = () => {
    const rawGroup = this.getActiveGroup();
    const group = rawGroup?.TargetVariable1 || rawGroup;
    if (group?.type !== "likert_grid") return null;
    const groupsById = this.getGroups();
    const map = {};
    for (const subVarId of (group.subVariables || [])) {
      const subVar = groupsById[subVarId];
      if (subVar?.weightCol && subVar?.keys) {
        for (const key of subVar.keys) map[key] = subVar.weightCol;
      }
    }
    return Object.keys(map).length > 0 ? map : null;
  };

  fetchMetrics = (level) => {
    fetchMetrics({
      level,
      props: this.props,
      setState: this.setState.bind(this),
      getActiveGroup: this.getActiveGroup,
      getFilters: this.getFilters,
      getConstCode: () => (level === "SA3" || level === "SA2") ? (this.state.selectedConstCode ?? null) : null,
      getKeyWeightMap: this.getKeyWeightMap,
      requestKey: this.requestKey,
      _internals: this._internals,
      tableFromIPC,
      rowsFromTable,
    });
  };

  restyle = () => {
    restyleGeo({
      geoData: this.state.geoData,
      metricRows: this.state.metricRows,
      group: this.getActiveGroup(),
      props: this.props,
      setStyledGeo: (geo) => this.setState({ styledGeo: geo }),
      getGroups: this.getGroups,
      buildLabelsAndColors,
      makeHeatScale,
      make2pointHeatScale,
      onRankedUpdate: this.props.onRankedUpdate,
      waveSelect: this.props.waveSelect,
    });
  };

  buildArrows = () => {
    const { compareWaves, compareKey } = this.props;
    if (!compareWaves || !this.state.centroids || !this.state.metricRows) {
      this.setState({ compareArrows: null });
      return;
    }
    const valueKey = compareKey || this.getActiveGroup()?.keys?.[0] || null;
    const arrows = buildCompareArrows({
      centroidMap: this.state.centroids,
      deltaRows: this.state.metricRows,
      valueKey,
    });
    this.setState({ compareArrows: arrows });
  };

  getFeatureBounds = (feature) => {
    const coords = [];
    const collect = (c) => {
      if (typeof c[0] === "number") { coords.push(c); return; }
      c.forEach(collect);
    };
    try { collect(feature.geometry.coordinates); } catch { return null; }
    if (!coords.length) return null;
    const lngs = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
  };

  zoomToAreaId = (areaId) => {
    const fc = this.state.styledGeo || this.state.geoData;
    if (!fc?.features) return;
    const feat = fc.features.find((f) =>
      String(f.properties?.area_id) === String(areaId) ||
      String(f.properties?.area_cd) === String(areaId)
    );
    if (!feat) return;
    const bounds = this.getFeatureBounds(feat);
    if (bounds) this.mapRef.current?.getMap()?.fitBounds(bounds, { padding: 40, duration: 500 });
    const areaName = feat.properties?.area_name || String(areaId);
    const selected = {
      key: "area_id",
      value: feat.properties.area_id,
      name: areaName,
      level: this.props.geoLevel || "electorate",
    };
    this.setState({
      selectedFeature: selected,
      selectedBounds: bounds,
      ...(this.props.geoLevel === "electorate"
        ? { selectedConstCode: feat.properties.area_id, selectedConstName: areaName }
        : {}),
    });
  };

  clearSelection = () => {
    this.setState({ selectedFeature: null, selectedBounds: null, selectedConstCode: null, selectedConstName: null });
    if (this.props.geoLevel === "SA2" || this.props.geoLevel === "SA3") {
      this.props.onGeoLevelChange?.("electorate");
    }
    this.fitNZ();
  };

  onMapClick = (e) => {
    const features = e.features;
    if (!features?.length) {
      this.setState({ selectedFeature: null, selectedBounds: null });
      return;
    }
    const feat = features[0];
    const props = feat.properties;
    const areaId = props?.area_id;
    const areaName = props?.area_name || String(areaId);
    const level = this.props.geoLevel || "electorate";
    const selected = { key: "area_id", value: areaId, name: areaName, level };
    const bounds = this.getFeatureBounds(feat);
    if (bounds) {
      this.mapRef.current?.getMap()?.fitBounds(bounds, { padding: 40, duration: 500 });
    }
    this.setState(
      {
        selectedFeature: selected,
        selectedBounds: bounds,
        ...(level === "electorate"
          ? { selectedConstCode: areaId, selectedConstName: areaName }
          : {}),
      },
      () => {
        if (level === "electorate") {
          this.props.onGeoLevelChange?.("SA3");
        }
      }
    );
  };

  onMouseMove = (e) => {
    if (e.features?.length) {
      const props = e.features[0].properties;
      // Deserialise JSON strings stored on feature properties
      const hProps = { ...props };
      ["_allValuesSorted", "_allValuesByKey", "_allValues"].forEach((k) => {
        if (typeof hProps[k] === "string") {
          try { hProps[k] = JSON.parse(hProps[k]); } catch {}
        }
      });
      this.setState({ hoverInfo: { x: e.point.x, y: e.point.y, props: hProps } });
      this.props.handlehoverAreaChange?.(props?.area_id);
    } else {
      this.setState({ hoverInfo: null });
      this.props.handlehoverAreaChange?.(null);
    }
  };

  onStyleLoad = () => {
    this.setState({ styleLoaded: true });
    const map = this.mapRef.current?.getMap?.();
    if (map) {
      registerArrowImage(map);
      map.on("styleimagemissing", (e) => {
        if (e?.id === "compare-arrow") registerArrowImage(map);
      });
    }
  };

  render() {
    const { compareWaves, showArrows } = this.props;
    const { geoData, styledGeo, metricRows, selectedConstCode, hoverInfo, styleLoaded, selectedFeature, compareArrows } = this.state;
    let displayGeo = styledGeo || geoData;
    // When drilled into a constituency, hide SA3/SA2 features that have no metric data
    // (backend filtered by constCode so only constituency features return rows)
    if (selectedConstCode != null && metricRows?.length && displayGeo?.features) {
      const metricIds = new Set(metricRows.map((r) => String(r.area_id)));
      displayGeo = { ...displayGeo, features: displayGeo.features.filter((f) => metricIds.has(String(f.properties?.area_id))) };
    }
    const map = this.mapRef.current?.getMap?.();
    const activeGroup = this.getActiveGroup();
    const effectiveGroup = activeGroup?.TargetVariable1 || activeGroup;

    return (
      <div style={{ height: "100%", width: "100%", position: "relative" }}>
        <ReactMap
          ref={this.mapRef}
          initialViewState={{ bounds: NZ_BOUNDS, fitBoundsOptions: { padding: 40 } }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={MAP_STYLE}
          mapboxAccessToken={MAPBOX_TOKEN}
          interactiveLayerIds={["geo-fill"]}
          renderWorldCopies={true}
          onClick={this.onMapClick}
          onMouseMove={this.onMouseMove}
          onLoad={this.onStyleLoad}
        >
          {displayGeo?.features && (
            <Source id="geo" type="geojson" data={displayGeo}>
              <Layer
                id="geo-fill"
                type="fill"
                paint={{
                  "fill-color": ["coalesce", ["get", "fillColor"], "#f5f5f5"],
                  "fill-opacity": 0.8,
                }}
              />
              <Layer
                id="geo-outline"
                type="line"
                paint={{ "line-color": "#555", "line-width": 0.4, "line-opacity": 0.5 }}
              />
              <Layer
                id="geo-hover"
                type="line"
                paint={{ "line-color": "#fff", "line-width": 2 }}
                filter={
                  hoverInfo?.props?.area_id != null
                    ? ["==", ["get", "area_id"], hoverInfo.props.area_id]
                    : ["==", ["get", "__none__"], "__none__"]
                }
              />
              <Layer
                id="geo-selected-halo"
                type="line"
                paint={{ "line-color": "#fff", "line-width": 4 }}
                filter={
                  selectedFeature?.value != null
                    ? ["==", ["get", "area_id"], selectedFeature.value]
                    : ["==", ["get", "__none__"], "__none__"]
                }
              />
              <Layer
                id="geo-selected"
                type="line"
                paint={{ "line-color": "#1976d2", "line-width": 2.5 }}
                filter={
                  selectedFeature?.value != null
                    ? ["==", ["get", "area_id"], selectedFeature.value]
                    : ["==", ["get", "__none__"], "__none__"]
                }
              />
            </Source>
          )}

          {styleLoaded && compareWaves && compareArrows?.features?.length > 0 && showArrows !== false && (
            <Source id="compare-arrows" type="geojson" data={compareArrows}>
              <Layer
                id="compare-arrows-layer"
                type="symbol"
                layout={{
                  "icon-image": "compare-arrow",
                  "icon-rotate": ["get", "angle"],
                  "icon-size": ["get", "size"],
                  "icon-allow-overlap": true,
                  "icon-ignore-placement": true,
                  "icon-rotation-alignment": "map",
                  "icon-anchor": "center",
                }}
                paint={{
                  "icon-color": ["get", "color"],
                  "icon-opacity": ["get", "opacity"],
                }}
              />
            </Source>
          )}
        </ReactMap>

        <MapHoverPopup
          hoverInfo={hoverInfo}
          group={effectiveGroup}
          groups={this.getGroups()}
          themeColours={this.props.theme_colours}
          preferenceFilter={this.props.preferenceFilter}
          mapRef={this.mapRef}
        />

        <MapToolbar
          geoLevel={this.props.geoLevel}
          onGeoLevelChange={this.props.onGeoLevelChange}
          selectedAreaLabel={selectedFeature?.name || null}
          onClearSelection={this.clearSelection}
          PossibleAreas={this.props.PossibleAreas}
          onAreaSelect={this.props.onAreaSelect}
          selectedDemographics={this.props.selectedDemographics}
          onResetDemographics={this.props.onResetDemographics}
          zoomIn={() => map?.zoomIn()}
          zoomOut={() => map?.zoomOut()}
          resetView={this.fitNZ}
          waveOptions={this.props.waveOptions}
          waveSelect={this.props.waveSelect}
          handlewaveSelectChange={this.props.handlewaveSelectChange}
          showArrows={this.props.showArrows}
          onShowArrowsToggle={this.props.onShowArrowsToggle}
          compareWaves={this.props.compareWaves}
          onCompareWavesChange={this.props.onCompareWavesChange}
          compareKey={this.props.compareKey}
          onCompareKeyChange={this.props.onCompareKeyChange}
          active_group={this.getActiveGroup()}
          actionMode={this.props.ActionMode}
          metricRowsElectorates={this.state.metricRowsElectorates}
        />
      </div>
    );
  }
}

export default MapPage;
